/**
 * Prompts List Page
 *
 * Main prompts management page with filtering and edit actions.
 * Note: Prompts are added via migrations, not created via UI.
 *
 * Acceptance Criteria: AC 3
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePrompts, type PromptFilters, type PromptResponse } from '../services/promptsApi';
import { useInitialData } from 'contexts/InitialDataContext';

export function PromptsListView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState<PromptFilters>({});

  const [groupFilter, setGroupFilter] = useState<string>(searchParams.get('group') || '');
  const [modelFilter, setModelFilter] = useState<string>(searchParams.get('model') || '');
  const [languageFilter, setLanguageFilter] = useState<string>(searchParams.get('language') || '');

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { state: initialDataState } = useInitialData();
  const { data: prompts, isLoading, error } = usePrompts(filters);

  const languages = initialDataState.data?.languages || [];

  // Debounce search with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      updateSearchParams({ search: searchTerm });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, updateSearchParams]);

  // Update filters when debounced search changes
  useEffect(() => {
    setFilters({
      promptKey: debouncedSearch || undefined,
    });
  }, [debouncedSearch]);

  const handleEditPrompt = (promptKey: string) => {
    const params = searchParams.toString();
    navigate(`/prompts/${promptKey}/edit`, {
      state: { searchParams: params ? `?${params}` : '' },
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Client-side filtering for model and languages
  const filteredPrompts =
    prompts?.filter((prompt) => {
      // Group filter
      if (groupFilter && prompt.promptGroup !== groupFilter) {
        return false;
      }

      // Model filter
      if (modelFilter && prompt.modelConfig.model !== modelFilter) {
        return false;
      }

      // Language filter
      if (
        languageFilter &&
        !prompt.promptTemplates.some((template) => template.languageCode === languageFilter)
      ) {
        return false;
      }

      // Search filter (prompt_key or description)
      const searchLower = debouncedSearch.toLowerCase();
      if (searchLower) {
        const matchesKey = prompt.promptKey.toLowerCase().includes(searchLower);
        const matchesDesc = prompt.description?.toLowerCase().includes(searchLower);
        return matchesKey || matchesDesc;
      }

      return true;
    }) || [];

  // Extract unique groups and models for filter dropdowns
  const uniqueGroups = Array.from(
    new Set(prompts?.map((p) => p.promptGroup).filter(Boolean) || [])
  ).sort();
  const uniqueModels = Array.from(new Set(prompts?.map((p) => p.modelConfig.model) || []));

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateDescription = (text: string | null, maxLength: number = 80): string => {
    if (!text) return 'No description';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const getLanguageName = (code: string): string => {
    const lang = languages.find((l) => l.code === code);
    return lang ? lang.name : code.toUpperCase();
  };

  const renderLanguageBadges = (prompt: PromptResponse) => {
    const languages = prompt.promptTemplates.map((t) => t.languageCode);
    const maxVisible = 5;

    if (languages.length <= maxVisible) {
      return (
        <>
          {languages.map((lang) => (
            <span
              key={lang}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
              title={getLanguageName(lang)}
            >
              {lang.toUpperCase()}
            </span>
          ))}
        </>
      );
    }

    const visibleLanguages = languages.slice(0, maxVisible);
    const remainingCount = languages.length - maxVisible;
    const remainingLanguagesNames = languages
      .slice(maxVisible)
      .map((code) => `${getLanguageName(code)} (${code.toUpperCase()})`)
      .join(', ');

    return (
      <>
        {visibleLanguages.map((lang) => (
          <span
            key={lang}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
            title={getLanguageName(lang)}
          >
            {lang.toUpperCase()}
          </span>
        ))}
        <span
          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded cursor-help"
          title={`Additional languages: ${remainingLanguagesNames}`}
        >
          +{remainingCount} more
        </span>
      </>
    );
  };

  if (isLoading || initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading prompts</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">LLM Prompt Management</h1>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Prompts are added via database migrations and cannot be created or
          deleted through the UI. You can edit existing prompts to update templates, model
          configurations, and descriptions.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by prompt key or description..."
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="search-input"
          />

          {/* Group Filter */}
          <select
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value);
              updateSearchParams({ group: e.target.value });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="group-filter"
          >
            <option value="">All Groups</option>
            {uniqueGroups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>

          {/* Model Filter */}
          <select
            value={modelFilter}
            onChange={(e) => {
              setModelFilter(e.target.value);
              updateSearchParams({ model: e.target.value });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="model-filter"
          >
            <option value="">All Models</option>
            {uniqueModels.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>

          {/* Language Filter */}
          <select
            value={languageFilter}
            onChange={(e) => {
              setLanguageFilter(e.target.value);
              updateSearchParams({ language: e.target.value });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="language-filter"
          >
            <option value="">All Languages</option>
            {languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Empty State */}
      {filteredPrompts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg mb-4">
            {prompts?.length === 0
              ? 'No prompts found in the database. Prompts are added via migrations.'
              : 'No prompts match your current filters.'}
          </p>
          {languageFilter || modelFilter || groupFilter || debouncedSearch ? (
            <button
              onClick={() => {
                setGroupFilter('');
                setLanguageFilter('');
                setModelFilter('');
                setSearchTerm('');
                setSearchParams({}, { replace: true });
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              data-testid="clear-filters-button"
            >
              Clear All Filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {/* Prompts Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" data-testid="prompts-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prompt Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Languages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Config
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPrompts.map((prompt) => (
                  <tr key={prompt.id} data-testid={`prompt-row-${prompt.promptKey}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{prompt.promptKey}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                        {prompt.promptGroup}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900" title={prompt.description || ''}>
                        {truncateDescription(prompt.description)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">{renderLanguageBadges(prompt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                        {prompt.provider}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{prompt.modelConfig.model}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="group relative inline-block">
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
                          data-testid={`config-button-${prompt.promptKey}`}
                        >
                          View Config
                        </button>
                        <div className="hidden group-hover:block absolute z-50 left-0 mt-2 w-96 bg-white border border-gray-300 rounded-lg shadow-xl p-4">
                          <div className="text-xs font-semibold text-gray-700 mb-2">
                            Model Configuration
                          </div>
                          <pre
                            className="bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto text-xs"
                            data-testid={`config-popup-${prompt.promptKey}`}
                          >
                            <code>{JSON.stringify(prompt.modelConfig, null, 2)}</code>
                          </pre>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(prompt.updatedAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEditPrompt(prompt.promptKey)}
                        className="text-blue-600 hover:text-blue-800"
                        data-testid={`edit-button-${prompt.promptKey}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
